<script>
  import { onMount, tick } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { useNavigate } from "svelte-navigator";
  import { ROUTES } from "@utils/routes.js";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { Modal as DangerModal } from "carbon-components-svelte";
  import Header from "@components/Header.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import PhaseMessageLanguage from "../components/PhaseMessageLanguage.svelte";
  import {
    START_TASK_REGISTRATION_MESSAGES,
    START_TASK_REGISTRATION_CONSENTS,
  } from "./phaseMessages.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import {
    InfoBlock,
    TextInput,
    TextArea,
    SectionWrapper,
    Tooltip,
    Button
  } from "paxray-components-svelte";
  import Toggle from "@components/Toggle.svelte";
  import SidePanelToggle from "lib/components/SidePanelToggle.svelte";

  const { updateProjectStep, markStepAsCompleted, isStepCompleted } =
    projectListStore;
  const urlParams = useUrlParams();
  const navigate = useNavigate();
  const step = PROJECT_STAGES.TASK_REGISTRATION.steps.START_TASK_REG;
  const stage = PROJECT_STAGES.TASK_REGISTRATION.id;

  let language = "en";
  let message = START_TASK_REGISTRATION_MESSAGES.en;
  let confirmmessage = START_TASK_REGISTRATION_CONSENTS.en;
  let editingMessage = false;
  let openDangerModal = false;

  const defaultTaskRegistrationSettings = {
    EnableTaskReview: true,
    ShowProcess: true,
    ShowSubProcess: true,
    ShowTask: true,
    ShowSubTask: true,
    ShowName: true,
    UIElements: true,
    Screenshots: false,
  };
  let taskRegistrationSettings = { ...defaultTaskRegistrationSettings };
  // Step mode for the PDD click-through: one picture per click and the typed values.
  // Raw screen content, meant for a deliberately configured second run - off by default.
  let stepScreenshots = false;

  // Top-level recorder switches for element capture beyond plain UI Automation.
  // Off by default: each one costs extra calls into the recorded application.
  const defaultElementCaptureSettings = {
    SAPElement: false,
    JavaElement: false,
    HtmlInfo: false,
  };
  let elementCaptureSettings = { ...defaultElementCaptureSettings };

  const elementCaptureSettingDefinitions = [
    {
      key: "SAPElement",
      label: "SAP Elements",
      tooltip:
        "Captures SAP GUI element details (id, type, text, tooltip) through SAP GUI Scripting for clicks in SAP.",
    },
    {
      key: "JavaElement",
      label: "Java Elements",
      tooltip:
        "Captures Java application element details (role, states, description) through the Java Access Bridge.",
    },
    {
      key: "HtmlInfo",
      label: "HTML Elements",
      tooltip:
        "Captures HTML tag, input type and name attribute of clicked web elements in Chromium browsers, Electron and WebView2 apps. The server must accept the html columns in ui_element_logs first.",
    },
  ];

  const taskRegistrationSettingDefinitions = [
    {
      key: "EnableTaskReview",
      label: "Show Task Review",
      tooltip:
        "Shows the Task Review screen after a registration. When disabled, the client uses the simplified name-entry and save flow instead; the review options below are hidden.",
    },
    {
      key: "ShowName",
      label: "Show Name Field",
      tooltip: "Shows the optional name field in Task Review.",
    },
    {
      key: "ShowProcess",
      label: "Show Process Selection",
      tooltip: "Shows the top-level process selector in Task Review.",
    },
    {
      key: "ShowSubProcess",
      label: "Show Subprocess Selection",
      tooltip: "Shows the subprocess selector in Task Review.",
    },
    {
      key: "ShowTask",
      label: "Show Task Selection",
      tooltip: "Shows the task selector in Task Review.",
    },
    {
      key: "ShowSubTask",
      label: "Show Subtask Selection",
      tooltip: "Shows the subtask selector in Task Review.",
    },
    {
      key: "UIElements",
      label: "Show UI Elements",
      tooltip: "Shows the UI element level in Task Review.",
    },
    // Screenshots are not supported by the client yet.
    // { key: "Screenshots", label: "Enable Screenshots", tooltip: "Enables screenshots to be transferred for Task Registration." },
  ];

  let stepActionsRef;
  let stepCompleted = false;
  let stepCompletedProject;
  let contactLoadedProject;
  let projectContacts = [];
  let settingsOpen = false;
  let settingsOpenBeforeGuide = false;
  let registrationGuideActive = false;
  let showRegistrationGuide = false;
  let registrationGuideStep = 0;
  let registrationGuideSteps = [];
  let registrationTourMounted = false;

  onMount(() => {
    registrationTourMounted = true;
    const unregisterTour = registerViewTour(startRegistrationGuide);
    return () => {
      registrationTourMounted = false;
      unregisterTour();
    };
  });

  $: if (registrationGuideActive) {
    if (showRegistrationGuide) {
      settingsOpen = Boolean(
        registrationGuideSteps[registrationGuideStep]?.showSettings,
      );
    } else {
      settingsOpen = settingsOpenBeforeGuide;
      registrationGuideActive = false;
    }
  }

  async function startRegistrationGuide() {
    if (registrationGuideActive || openDangerModal) return;
    settingsOpenBeforeGuide = settingsOpen;
    settingsOpen = true;
    await tick();
    if (!registrationTourMounted) return;
    registrationGuideSteps = [
      {
        target: '[data-guide="registration-overview"]',
        title: "Prepare task registration",
        text: "Task registration lets participants document the tasks they work on during Discovery. Prepare the participant message, consent text and settings before starting.",
      },
      {
        target: '[data-guide="registration-message"] .phase_message_language',
        title: "Choose the participant language",
        text: "Choose the language for the participant message and consent text. Changing the language replaces both texts with the corresponding templates, so choose it before making edits.",
      },
      {
        target: '[data-guide="registration-message"]',
        title: "Review the participant message",
        text:
          !stepCompleted && privacyDetailsMissing
            ? "The participant message still contains placeholders for the Controller or Data Protection Officer. Click “Edit message” and replace them with the appropriate contact details before starting task registration."
            : "This message explains task registration to participants before they begin. Click “Edit message” to adapt it to your project. Check that the Controller and Data Protection Officer contact details are complete and that no placeholders remain.",
      },
      {
        target: '[data-guide="registration-consent"]',
        title: "Check the consent text",
        text: "Review the text shown beside the participant consent checkbox. Use “Edit message” to change it alongside the participant message. Make sure both texts are consistent before continuing.",
      },
      {
        target: '[data-guide="registration-review"]',
        title: "Review expert settings",
        showSettings: true,
        text: "These are expert settings. Keep the defaults unless your project needs a different setup. Task Review lets participants check and describe their task before saving it. Choose which fields they see. “Step-by-step Screenshots” adds an image of the application window at each click and includes entered values to help document the task step by step. Enable it only for a prepared documentation run.",
      },
      {
        target: '[data-guide="registration-elements"]',
        title: "Choose element capture options",
        showSettings: true,
        text: "Enable “SAP Elements” if your team uses SAP, “Java Elements” if they use Java applications, and “HTML Elements” if they use SaaS applications in the browser.",
      },
      {
        target:
          '[data-guide="start-registration"] > button, [data-guide="start-registration"] > .completed-badge',
        title: "Start task registration",
        text: stepCompleted
          ? "Task registration has already started for this project. Participants can start and stop individual task registrations using “Register Task”. Return to Project Progress to review team progress and the next Discovery steps."
          : "Check your message, consent text and settings, then click “Start Task Registration” and confirm. This starts the phase; participants still start and stop individual task registrations using “Register Task”. Starting the phase cannot be undone.",
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    registrationGuideStep = 0;
    showRegistrationGuide = registrationGuideSteps.length > 0;
    registrationGuideActive = true;
  }

  $: ({ idproject } = $urlParams);
  $: ({ currentProjectContacts } = $projectListStore);
  $: if (idproject) {
    message = fillProjectContacts(message, currentProjectContacts);
  }
  $: missingControllerInfo =
    message.includes("{ADD CONTROLLER INFO HERE}") ||
    message.includes("{COMPANY}");
  $: missingDataProtectionOfficerInfo =
    message.includes("{ADD DATA PROTECTION OFFICER INFO HERE}") ||
    message.includes("{PERSON}");
  $: privacyDetailsMissing =
    missingControllerInfo || missingDataProtectionOfficerInfo;
  $: missingPrivacyMessage = [
    missingControllerInfo && "the Controller",
    missingDataProtectionOfficerInfo && "the Data Protection Officer",
  ]
    .filter(Boolean)
    .join(" and ");
  $: if (idproject && idproject !== stepCompletedProject) {
    checkStepCompleted(idproject);
  }

  function fillProjectContacts(text, contacts) {
    const firstContact = contacts?.[0];
    const secondContact = contacts?.[1];
    text = replaceContactPlaceholder(text, firstContact, [
      "{ADD CONTROLLER INFO HERE}",
      "{COMPANY}",
    ]);
    return replaceContactPlaceholder(text, secondContact, [
      "{ADD DATA PROTECTION OFFICER INFO HERE}",
      "{PERSON}",
    ]);
  }

  function formatContact(contact) {
    const name = contact?.name?.trim();
    const email = contact?.email?.trim();
    return [name, email && `(${email})`].filter(Boolean).join(" ");
  }

  function replaceContactPlaceholder(text, contact, placeholders) {
    const details = formatContact(contact);
    if (!details) return text;

    for (const placeholder of placeholders) {
      const escapedPlaceholder = placeholder.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      const span = new RegExp(
        `<span[^>]*>[^<]*${escapedPlaceholder}[^<]*<\\/span>`,
      );
      if (span.test(text)) return text.replace(span, details);
      if (text.includes(placeholder)) return text.replace(placeholder, details);
    }

    return text;
  }

  async function checkStepCompleted(idproject) {
    stepCompletedProject = idproject;
    stepCompleted = await isStepCompleted(idproject, step, stage);
  }

  async function startTaskRegistration() {
    console.log("Starting task registration");
    const res = await fetchWithSession(
      `/api/damprojects/startAnonymizationRecording`,
      {
        method: "POST",
        body: JSON.stringify({
          idproject: idproject,
          message: message,
          confirmmessage: confirmmessage,
          TaskRegistration: {
            IsEnabled: true,
            ...taskRegistrationSettings,
          },
          ElementCapture: { ...elementCaptureSettings },
          StepScreenshots: { IsEnabled: stepScreenshots },
        }),
      },
    );
    const data = await res.json();
    if (data?.ok) {
      await handleMarkAsCompleted();
    }
    openDangerModal = false;
  }

  async function handleMarkAsCompleted() {
    const data = await markStepAsCompleted(idproject, step, stage);
    if (data?.ok) {
      stepCompleted = true;
      stepActionsRef?.refresh();
      navigate(`${ROUTES.dam.progress}?idproject=${idproject}`);
    }
  }
</script>

<Header
  section=""
  area="Discovery"
  breadcrumb="Start Task Registration"
  tooltipText="Start task registration and prepare the participant message and consent text shown before recording begins."
>
  {#if $uiFeatureStore.projectProgress.startTaskRegistration}
    <div data-guide="start-registration">
      <ProjectStepActions
        bind:this={stepActionsRef}
        {idproject}
        {step}
        {stage}
        disabled={privacyDetailsMissing}
        showGoBackButton={false}
        bind:completed={stepCompleted}
        text="Start Task Registration"
        size="sm"
        confirmBeforeComplete={false}
        onClick={() => (openDangerModal = true)}
      />
    </div>
  {/if}
</Header>

<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="section_content" slot="main">
    <div class="main">
      <div data-guide="registration-overview">
        <InfoBlock
          type="info"
          heading="Start Task Registration"
          message="Configure the message and consent text that participants will see before Task Registration begins. When you are ready, click &quot;Start Task Registration&quot; to start the task registration phase."
          showClose={false}
        />
      </div>
      {#if !stepCompleted && privacyDetailsMissing}
        <InfoBlock
          type="warning"
          heading="Complete the contact information"
          message={`Please add ${missingPrivacyMessage} to the message before continuing.`}
          showClose={false}
        />
      {/if}
      <div class="cell">
        <div class="field_group" data-guide="registration-message">
          <div class="field_label">
            <PhaseMessageLanguage
              messages={START_TASK_REGISTRATION_MESSAGES}
              consents={START_TASK_REGISTRATION_CONSENTS}
              bind:language
              bind:value={message}
              bind:consent={confirmmessage}
            />
            <Button
              size="field"
              kind="secondary"
              on:click={() => (editingMessage = !editingMessage)}
            >
              {editingMessage ? "Done editing" : "Edit message"}
            </Button>
          </div>
          {#if editingMessage}
            <TextArea
              placeholder="Enter a message for the users"
              persons={currentProjectContacts}
              bind:value={message}
              richText
              labelText="Main Message"
              tooltipText="This is the main message shown to users before Task Registration starts."
            />
          {:else}
            <div class="message-preview">
              {@html message}
            </div>
          {/if}
        </div>
        <div data-guide="registration-consent">
          {#if editingMessage}
            <TextInput
              labelText="Consent Checkbox Text"
              tooltipText="This is the confirmation text shown next to the consent checkbox."
              bind:value={confirmmessage}
            />
          {:else}
            <div class="consent-preview">
              <span class="bx--label">Consent Checkbox Text</span>
              <p>{confirmmessage}</p>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
  <SidePanelToggle
    slot="sidePanel"
    label="Settings"
    title="Task Registration Settings"
    width="24rem"
    bind:open={settingsOpen}
  >
    <div class="task-registration-settings" data-guide="registration-review">
      {#each taskRegistrationSettingDefinitions as setting}
        {#if setting.key === "EnableTaskReview" || taskRegistrationSettings.EnableTaskReview}
          <div class="setting-row" data-guide="registration-review-option">
            <div
              class="setting-label"
              class:dependent-setting={setting.key !== "EnableTaskReview"}
            >
              <span>{setting.label}</span>
              <Tooltip
                text={setting.tooltip}
                direction="right"
                align="center"
              />
            </div>
            <Toggle bind:active={taskRegistrationSettings[setting.key]} />
          </div>
        {/if}
      {/each}
      <div class="setting-row" data-guide="registration-pictures">
        <div class="setting-label">
          <span>Step-by-step Screenshots</span>
          <Tooltip
            text="Expert setting: adds a screenshot of the application window at each click and includes entered values to document a task step by step. Screenshots contain the original window content. Enable only for a prepared documentation run."
            direction="right"
            align="center"
          />
        </div>
        <Toggle bind:active={stepScreenshots} />
      </div>
    </div>
    <div class="settings-group-title">Element Capture</div>
    <div class="task-registration-settings" data-guide="registration-elements">
      {#each elementCaptureSettingDefinitions as setting}
        <div class="setting-row">
          <div class="setting-label">
            <span>{setting.label}</span>
            <Tooltip text={setting.tooltip} direction="right" align="center" />
          </div>
          <Toggle bind:active={elementCaptureSettings[setting.key]} />
        </div>
      {/each}
    </div>
  </SidePanelToggle>
</SectionWrapper>

<SpotlightGuide
  bind:open={showRegistrationGuide}
  bind:currentStep={registrationGuideStep}
  steps={registrationGuideSteps}
  allowInteraction={false}
/>

<DangerModal
  danger
  bind:open={openDangerModal}
  preventCloseOnClickOutside
  modalHeading="Start Task Registration?"
  primaryButtonText="Start Task Registration"
  secondaryButtonText="Cancel"
  on:click:button--primary={startTaskRegistration}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <p>
    Starting Task Registration will begin the registration phase for this
    project. Individual recordings can still be started and stopped using the
    “Register Task” function. Please make sure the participant message and
    consent text are correct before continuing. This action cannot be undone.
  </p>
</DangerModal>

<style>
  .section_content,
  .main {
    max-width: 100%;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .main {
    flex: 1 1 auto;
    min-height: 0;
  }

  .cell {
    flex: 0 0 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .field_group {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    flex: 0 0 auto;
    min-height: 0;
  }

  .field_label {
    display: flex;
    align-items: flex-end;
    gap: var(--xs);
  }

  .field_label :global(.phase_message_language) {
    margin-left: 0;
  }

  .message-preview,
  .consent-preview p {
    padding: 1rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    overflow-wrap: anywhere;
  }

  .consent-preview {
    margin-top: var(--control-gap);
    overflow-wrap: anywhere;
  }

  .consent-preview p {
    margin: 0;
  }

  .field_group :global(.richtext) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.4rem;
    min-height: 1.25rem;
  }

  .field_label :global(.bx--label) {
    display: inline-flex;
    align-items: center;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
    letter-spacing: 0.02em;
    color: var(--pax-black);
    margin: 0;
    text-align: left;
  }

  .settings-group-title {
    margin-top: 1.25rem;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--pax-black);
  }

  .task-registration-settings {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 0.75rem;
    width: 100%;
  }

  .setting-row {
    display: contents;
  }

  .setting-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 2.25rem;
    line-height: 1.35;
    white-space: normal;
  }

  .setting-label.dependent-setting {
    padding-left: 1rem;
  }

  .setting-row :global(.toggle_wrapper) {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 2rem;
    min-height: 2.25rem;
  }

  .message-preview :global(b),
  .message-preview :global(strong) {
    font-weight: bold;
  }

  .message-preview :global(i),
  .message-preview :global(em) {
    font-style: italic;
  }

  .message-preview :global(u) {
    text-decoration: underline;
  }
</style>
